import React, { useState, useEffect, useContext } from "react";
import createAuth0Client from "@auth0/auth0-spa-js";

const DEFAULT_REDIRECT_CALLBACK = () =>
  window.history.replaceState({}, document.title, window.location.pathname);

/**
 * Merges permissions from ID token claims to ensure all permissions from all roles are included
 * @param {Object} user - The user object from Auth0
 * @param {Object} claims - The ID token claims
 * @returns {Object} - User object with merged permissions
 */
const mergePermissionsFromClaims = (user, claims) => {
  if (!user || !claims) return user;

  const namespace = process.env.REACT_APP_NAMESPACE || '';
  // Handle namespace with or without trailing slash
  const permissionsKey = namespace.endsWith('/') 
    ? `${namespace}permissions` 
    : `${namespace}/permissions`;
  
  // Get permissions from user object (if already set)
  const userPermissions = user[permissionsKey] || [];
  
  // Get permissions from ID token claims (which should include all roles)
  // Also check alternative key formats in case namespace format differs
  const claimsPermissions = claims[permissionsKey] || 
                            claims[`${namespace}permissions`] || 
                            claims['permissions'] || 
                            [];
  
  // Merge permissions and remove duplicates
  const mergedPermissions = [...new Set([...userPermissions, ...claimsPermissions])];
  
  // Update user object with merged permissions
  return {
    ...user,
    [permissionsKey]: mergedPermissions
  };
};

export const Auth0Context = React.createContext();
export const useAuth0 = () => useContext(Auth0Context);
export const Auth0Provider = ({
  children,
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  ...initOptions
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState();
  const [user, setUser] = useState();
  const [auth0Client, setAuth0] = useState();
  const [loading, setLoading] = useState(true);
  const [popupOpen, setPopupOpen] = useState(false);

  useEffect(() => {
    const initAuth0 = async () => {
      try {
        const auth0FromHook = await createAuth0Client(initOptions);
        setAuth0(auth0FromHook);

        if (window.location.search.includes("code=") &&
          window.location.search.includes("state=")) {
          try {
            const { appState } = await auth0FromHook.handleRedirectCallback();
            onRedirectCallback(appState);
          } catch (error) {
            // Auth callback failed (e.g. consent_required) — fall through to unauthenticated state
          }
        }

        const isAuthenticated = await auth0FromHook.isAuthenticated();

        setIsAuthenticated(isAuthenticated);

        if (isAuthenticated) {
          const user = await auth0FromHook.getUser();
          try {
            const claims = await auth0FromHook.getIdTokenClaims();
            const mergedUser = mergePermissionsFromClaims(user, claims);
            setUser(mergedUser);
          } catch (error) {
            setUser(user);
          }
        }
      } catch (error) {
        setIsAuthenticated(false);
      }

      setLoading(false);
    };
    initAuth0();
    // eslint-disable-next-line
  }, []);

  const loginWithPopup = async (params = {}) => {
    setPopupOpen(true);
    try {
      await auth0Client.loginWithPopup(params);
    } catch (error) {
      // Error handled silently
    } finally {
      setPopupOpen(false);
    }
    const user = await auth0Client.getUser();
    // Get ID token claims to merge permissions from all roles
    try {
      const claims = await auth0Client.getIdTokenClaims();
      const mergedUser = mergePermissionsFromClaims(user, claims);
      setIsAuthenticated(true);
      setUser(mergedUser);
    } catch (error) {
      setIsAuthenticated(true);
      setUser(user);
    }
  };

  const handleRedirectCallback = async () => {
    setLoading(true);
    try {
      await auth0Client.handleRedirectCallback();
    } catch (error) {
      setLoading(false);
      setIsAuthenticated(false);
      return;
    }
    const user = await auth0Client.getUser();
    // Get ID token claims to merge permissions from all roles
    try {
      const claims = await auth0Client.getIdTokenClaims();
      const mergedUser = mergePermissionsFromClaims(user, claims);
      setLoading(false);
      setIsAuthenticated(true);
      setUser(mergedUser);
    } catch (error) {
      setLoading(false);
      setIsAuthenticated(true);
      setUser(user);
    }
  };
  

  return (
    <Auth0Context.Provider
      value={{
        isAuthenticated,
        user,
        auth0Client,
        loading,
        popupOpen,
        loginWithPopup,
        handleRedirectCallback,
        getIdTokenClaims: (...p) => auth0Client.getIdTokenClaims(...p),
        loginWithRedirect: (...p) => auth0Client.loginWithRedirect(...p),
        getTokenSilently: (...p) => auth0Client.getTokenSilently(...p),
        getTokenWithPopup: (...p) => auth0Client.getTokenWithPopup(...p),
        logout: (...p) => auth0Client.logout(...p)
      }}
    >
      {children}
    </Auth0Context.Provider>
  );
};